import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { adminMlmAPI, getApiError } from '../../services/api';
import {
  Search,
  Users,
  Trophy,
  RefreshCw,
  Globe,
  Activity,
  Plus,
  Play,
  Settings,
  ChevronRight
} from 'lucide-react';

const TABS = [
  'Gestor Management',
  'Rank Configuration',
  'Cycle Management',
  'Active Status',
  'Global Pool',
  'Degradation'
];

const AdminMLM = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const [gestores, setGestores] = useState([]);
  const [gestorSearch, setGestorSearch] = useState('');
  const [ranks, setRanks] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [activeStatus, setActiveStatus] = useState(null);
  const [poolRuns, setPoolRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState(null);

  const [newCycleName, setNewCycleName] = useState('');
  const [poolRunData, setPoolRunData] = useState({ pool_id: '', amount: '' });

  const cardClass = `${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 shadow-sm border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`;
  const tabClass = (isActive) =>
    `px-4 py-2.5 text-sm font-medium rounded-xl transition-all whitespace-nowrap ${
      isActive
        ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-md'
        : `${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'}`
    }`;

  const fetchTabData = async (tab) => {
    setLoading(true);
    setActionMsg(null);
    try {
      switch (tab) {
        case 0: {
          const res = await adminMlmAPI.getGestores({ search: gestorSearch || undefined });
          setGestores(res.data?.gestores || []);
          break;
        }
        case 1: {
          const res = await adminMlmAPI.getRankConfig();
          setRanks(res.data || []);
          break;
        }
        case 2: {
          const res = await adminMlmAPI.getCycles();
          setCycles(res.data?.results || res.data || []);
          break;
        }
        case 3: {
          const res = await adminMlmAPI.getActiveStatus();
          setActiveStatus(res.data);
          break;
        }
        case 4: {
          const res = await adminMlmAPI.getPoolRuns();
          setPoolRuns(res.data?.results || res.data || []);
          break;
        }
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTabData(activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 0) {
      const timer = setTimeout(() => fetchTabData(0), 300);
      return () => clearTimeout(timer);
    }
  }, [gestorSearch]);

  const handleAction = async (action, fn) => {
    setActionMsg(null);
    try {
      const res = await fn();
      setActionMsg({ type: 'success', text: res.data?.message || `${action} successful` });
      fetchTabData(activeTab);
    } catch (err) {
        setActionMsg({ type: 'error', text: getApiError(err, `${action} failed`) });
    }
  };

  const showMsg = () => {
    if (!actionMsg) return null;
    return (
      <div className={`mb-4 px-4 py-3 rounded-xl text-sm ${
        actionMsg.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}>
        {actionMsg.text}
      </div>
    );
  };

  const renderTab = () => {
    switch (activeTab) {
      case 0:
        return (
          <div className="space-y-4">
            <div className={cardClass}>
              <div className="flex gap-3 mb-4">
                <div className="flex-1 relative">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  <input
                    type="text"
                    value={gestorSearch}
                    onChange={(e) => setGestorSearch(e.target.value)}
                    placeholder="Search gestores by name, email or STTORA ID..."
                    className={`w-full pl-10 pr-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
              </div>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600" />
                </div>
              ) : gestores.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Name</th>
                        <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>STTORA ID</th>
                        <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Rank</th>
                        <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Status</th>
                        <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gestores.map((g, i) => (
                        <tr key={g.id || i} className={`border-b ${isDarkMode ? 'border-gray-700/50' : 'border-gray-100'}`}>
                          <td className={`py-3 px-4 font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{g.name || g.first_name || '—'}</td>
                          <td className={`py-3 px-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{g.sttora_id || '—'}</td>
                          <td className={`py-3 px-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{g.rank || '—'}</td>
                          <td className="py-3 px-4">
                            <span               className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                              g.is_active_biweekly || g.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {g.is_active_biweekly || g.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <button
                              onClick={() => navigate(`/admin/mlm/users/${g.id}/network`)}
                              className="inline-flex items-center gap-1 text-orange-600 hover:text-orange-800 text-sm font-medium"
                            >
                              View Network <ChevronRight className="w-3 h-3" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No gestores found</p>
              )}
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-4">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600" />
              </div>
            ) : ranks.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Rank Name</th>
                      <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Min Level</th>
                      <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Weak Leg VU</th>
                      <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Direct Referrals</th>
                      <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ranks.map((r, i) => (
                      <tr key={r.id || i} className={`border-b ${isDarkMode ? 'border-gray-700/50' : 'border-gray-100'}`}>
                        <td className={`py-3 px-4 font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{r.rank_name || r.name}</td>
                        <td className={`py-3 px-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{r.rank_level || r.min_level || '—'}</td>
                        <td className={`py-3 px-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{r.min_weak_leg_vu || r.weak_leg_vu_required || '—'}</td>
                        <td className={`py-3 px-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{r.min_direct_referrals || r.direct_referrals_required || '—'}</td>
                        <td className="py-3 px-4">
                          <button className="inline-flex items-center gap-1 text-orange-600 hover:text-orange-800 text-sm font-medium">
                            <Settings className="w-3 h-3" /> Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No rank configuration available</p>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Create Current Cycle</label>
                <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Creates a new biweekly cycle based on the current date</p>
                  className={`w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
              <button
                onClick={() => handleAction('Create cycle', () => adminMlmAPI.createCycle())}
                className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-medium inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> Create Current Cycle
              </button>
            </div>

            {showMsg()}

            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600" />
              </div>
            ) : cycles.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>ID</th>
                      <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Cycle</th>
                      <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Processed</th>
                      <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cycles.map((c, i) => (
                      <tr key={c.id || i} className={`border-b ${isDarkMode ? 'border-gray-700/50' : 'border-gray-100'}`}>
                        <td className={`py-3 px-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{c.id}</td>
                        <td className={`py-3 px-4 font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{c.year}-{String(c.month).padStart(2,'0')} C{c.cycle_number || `#${c.id}`}</td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                            c.is_processed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>{c.is_processed ? 'Processed' : 'Pending'}</span>
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => handleAction('Process cycle', () => adminMlmAPI.processCycle(c.id))}
                            disabled={c.is_processed}
                            className="inline-flex items-center gap-1 text-orange-600 hover:text-orange-800 text-sm font-medium disabled:opacity-50"
                          >
                            <Play className="w-3 h-3" /> Process
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No cycles yet</p>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            {showMsg()}
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600" />
              </div>
            ) : activeStatus ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={cardClass}>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Active</p>
                  <p className={`text-3xl font-bold text-green-500`}>{activeStatus?.stats?.active || activeStatus?.active || 0}</p>
                </div>
                <div className={cardClass}>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Inactive</p>
                  <p className={`text-3xl font-bold text-red-500`}>{activeStatus?.stats?.inactive || activeStatus?.inactive || 0}</p>
                </div>
                <div className={cardClass}>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Active Percentage</p>
                  <p className={`text-3xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                    {(() => {
                      const total = activeStatus?.stats?.active || activeStatus?.active || 0;
                      const checked = activeStatus?.stats?.total_checked || activeStatus?.total_checked || 1;
                      return ((total / checked) * 100).toFixed(1);
                    })()}%
                  </p>
                </div>
              </div>
            ) : (
              <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No active status data available</p>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            {showMsg()}
            <div className="flex gap-3">
              <button
                onClick={() => handleAction('Initialize pools', () => adminMlmAPI.initializePools())}
                className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-medium inline-flex items-center gap-2"
              >
                <Globe className="w-4 h-4" /> Initialize Pools
              </button>
              <button
                onClick={() => handleAction('Run pool', () => adminMlmAPI.runPool(poolRunData))}
                className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-medium inline-flex items-center gap-2"
              >
                <Play className="w-4 h-4" /> Run Pool Distribution
              </button>
            </div>

            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600" />
              </div>
            ) : poolRuns.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Date</th>
                      <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Pool</th>
                      <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Amount Distributed</th>
                      <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {poolRuns.map((run, i) => (
                      <tr key={run.id || i} className={`border-b ${isDarkMode ? 'border-gray-700/50' : 'border-gray-100'}`}>
                        <td className={`py-3 px-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {run.run_date ? new Date(run.run_date).toLocaleDateString() : run.created_at ? new Date(run.created_at).toLocaleDateString() : '—'}
                        </td>
                        <td className={`py-3 px-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{run.pool_name || `Pool #${run.pool_id}` || '—'}</td>
                        <td className={`py-3 px-4 font-medium text-green-500`}>${(run.total_pool_amount || run.amount_per_qualified || run.amount_distributed || 0).toFixed(2)}</td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                            run.is_paid ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>{run.is_paid ? 'Paid' : 'Pending'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No pool runs yet</p>
            )}
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            {showMsg()}
            <div className={cardClass}>
              <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Degradation Check</h3>
              <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Run degradation check to identify and process rank degradations based on current activity and volume requirements.
              </p>
              <button
                onClick={() => handleAction('Degradation check', () => adminMlmAPI.runDegradationCheck())}
                className="px-6 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-medium inline-flex items-center gap-2"
              >
                <Activity className="w-4 h-4" /> Run Degradation Check
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div>
        <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>MLM Administration</h1>
        <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Manage MLM system configuration and users</p>
      </div>

      <div className={`flex flex-wrap gap-2 p-1 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} rounded-2xl overflow-x-auto`}>
        {TABS.map((tab, i) => (
          <button key={tab} onClick={() => setActiveTab(i)} className={tabClass(activeTab === i)}>
            {tab}
          </button>
        ))}
      </div>

      {renderTab()}
    </motion.div>
  );
};

export default AdminMLM;
